import type { SlotOption } from '../models/entitlement/SlotOption';
import { guestSeatPriceCents } from './guestPricing';

export type RigClass = 'pro' | 'esports';

/** How much of the party a single simulator class can seat at one slot. */
export type ClassAvailabilityStatus = 'available' | 'limited' | 'sold-out';

export interface ClassAvailability {
  status: ClassAvailabilityStatus;
  count: number;
}

export const rigsFree = (slot: SlotOption, rig: RigClass): number =>
  (rig === 'pro' ? slot.proRigsAvailable : slot.esportsRigsAvailable);

/**
 * Per-class availability at a slot for a given party size — the same three-way split the public
 * funnel uses (`getClassAvailability` in wor-landing-pages), so a member sees the same rules here
 * as on the site they booked from before joining.
 *
 * - `available`: this class alone seats the whole party
 * - `limited`:   1..people-1 free, so some of the party fits and the rest go on the other class
 * - `sold-out`:  nothing free on this class
 */
export function getClassAvailability(
  slot: SlotOption, people: number, rig: RigClass,
): ClassAvailability {
  const count = rigsFree(slot, rig);
  if (count === 0) return { status: 'sold-out', count };
  if (count >= people) return { status: 'available', count };
  return { status: 'limited', count };
}

/**
 * A slot is bookable when the two classes TOGETHER seat the party. Mixing is normal — one Pro and
 * two Esports is a perfectly good three-seat booking — so judging a slot on either class alone
 * would hide times that are genuinely available.
 */
export const slotFitsParty = (slot: SlotOption, people: number): boolean =>
  slot.proRigsAvailable + slot.esportsRigsAvailable >= people;

/**
 * The cheapest total for the PAID seats at this slot, in cents, or undefined when the slot cannot
 * seat them or carries no quoted rates.
 *
 * Fills the cheaper class first up to what it has free, then spills onto the dearer one — which is
 * the cheapest arrangement available, and therefore the honest "from" figure. The member's own
 * entitlement seat is $0 and is never counted: `paidSeats` is the guest count.
 *
 * `guestDiscountBp` is the member tier's guest discount. It is taken off each SEAT's rate before
 * multiplying, exactly as the seat rows, the summary and the server's WalkInPricing do — rounding
 * once on the total can land a cent away from all three. Defaults to 0 (list price) for any caller
 * without a member tier.
 *
 * Display only. What is actually charged is the server's `amountMinor` on the 202.
 */
export function cheapestPaidSeatsCents(
  slot: SlotOption, paidSeats: number, memberRig?: RigClass, guestDiscountBp = 0,
): number | undefined {
  if (paidSeats <= 0) return 0;
  const pro = guestSeatPriceCents(slot.pricePerPersonCents, guestDiscountBp);
  const esports = guestSeatPriceCents(slot.esportsPricePerPersonCents, guestDiscountBp);
  if (typeof pro !== 'number' || typeof esports !== 'number') return undefined;

  // The member occupies a rig too. Without removing it the quote assumed the guests could have
  // every rig in the room, so at a slot with 1 Pro free it happily priced two Pro guest seats.
  let proFree = slot.proRigsAvailable;
  let esportsFree = slot.esportsRigsAvailable;
  if (memberRig === 'pro') proFree -= 1;
  else if (memberRig === 'esports') esportsFree -= 1;
  proFree = Math.max(0, proFree);
  esportsFree = Math.max(0, esportsFree);

  const cheap = pro <= esports
    ? { cents: pro, free: proFree }
    : { cents: esports, free: esportsFree };
  const dear = pro <= esports
    ? { cents: esports, free: esportsFree }
    : { cents: pro, free: proFree };

  if (cheap.free + dear.free < paidSeats) return undefined;

  const onCheap = Math.min(cheap.free, paidSeats);
  const onDear = paidSeats - onCheap;
  return onCheap * cheap.cents + onDear * dear.cents;
}

/** What a day chip in the strip shows. */
export type DayState = 'open' | 'full' | 'closed';

/**
 * A day is `closed` when the venue offers nothing at all, `full` when it opens but no slot can
 * seat the party, and otherwise `open`.
 *
 * The distinction matters to a member: "Closed" is never going to change, "Full" might if they
 * drop a guest. A single greyed chip with no reason answers neither question.
 */
export function dayState(slots: SlotOption[], people: number): DayState {
  if (slots.length === 0) return 'closed';
  return slots.some((s) => slotFitsParty(s, people)) ? 'open' : 'full';
}

/**
 * Moves a seat off a simulator class that has nothing free at this slot.
 *
 * Picking a time where the member's chosen class is sold out used to leave the seat on that class
 * and fail at the server with "That slot was just taken" — a misleading message for a slot that
 * was never available on that simulator. The UI must not offer a slot it cannot then book.
 *
 * Returns the class the seat should be on: unchanged when it still has rigs, the other class when
 * it does not, and unchanged when neither has any (the slot is blocked anyway, and silently
 * flipping a seat on a dead slot would be a confusing no-op).
 */
export function reconcileRigClass(
  slot: SlotOption, rig: RigClass,
): RigClass {
  if (rigsFree(slot, rig) > 0) return rig;
  const other: RigClass = rig === 'pro' ? 'esports' : 'pro';
  return rigsFree(slot, other) > 0 ? other : rig;
}

/**
 * Fits a whole party's simulator choices into what a slot actually has free.
 *
 * `reconcileRigClass` decides one seat at a time, so it cannot see that several seats are competing
 * for the same rigs: with 1 Pro free and three seats all set to Pro, every seat individually looked
 * fine and the booking was sent for 3 Pro rigs that do not exist.
 *
 * Preferences are honoured in order and the overflow moves to the other class. The order is the
 * seat order — the member first, then guests as listed — so the person who chose first keeps their
 * choice, which is the least surprising rule to explain.
 *
 * Returns the classes each seat should be on, same length and order as `preferred`. When the party
 * does not fit at all, preferences are returned unchanged: the slot is unbookable anyway, and
 * rewriting seats on a slot nobody can take would be noise.
 */
export function fitPartyToSlot(
  slot: SlotOption, preferred: RigClass[],
): RigClass[] {
  if (!slotFitsParty(slot, preferred.length)) return preferred;

  let pro = slot.proRigsAvailable;
  let esports = slot.esportsRigsAvailable;
  const take = (rig: RigClass): boolean => {
    if (rig === 'pro' && pro > 0) { pro -= 1; return true; }
    if (rig === 'esports' && esports > 0) { esports -= 1; return true; }
    return false;
  };

  return preferred.map((rig) => {
    if (take(rig)) return rig;
    const other: RigClass = rig === 'pro' ? 'esports' : 'pro';
    return take(other) ? other : rig;
  });
}

/**
 * Whether a seat may be switched onto `target` without exceeding what the slot has.
 *
 * Automatic fitting handles the seats a member has not touched, but the toggles stayed clickable
 * afterwards: with 1 Pro rig free you could still put a second seat on Pro and book rigs that do
 * not exist. A seat can move only when its target class has a rig free once every OTHER seat's
 * current choice is counted.
 *
 * `seatIndex` is excluded from that count because it is the one being moved — it gives its current
 * rig back as it leaves.
 */
export function canSeatUse(
  slot: SlotOption, seats: RigClass[], seatIndex: number, target: RigClass,
): boolean {
  if (seats[seatIndex] === target) return true;
  const usedByOthers = seats.reduce(
    (n, rig, i) => (i !== seatIndex && rig === target ? n + 1 : n),
    0,
  );
  return usedByOthers < rigsFree(slot, target);
}
